/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';

import ClearIcon from '@mui/icons-material/Clear';
import { useFiltersCard } from './hooks';
import { FilterTypeMapper } from '../../../Report/components/Filters/FilterTypeMapper.tsx';
import { Select } from '@ui';
import { theme } from '@store';
import {
  IPatientReport,
  ReportPatientReportType,
  ReportTimeInterval,
} from '@common/types';
import {
  patientReportTypeOptions,
  reportTimeIntervalOptions,
} from '../../../types.ts';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { Add, FilterAlt, Delete } from '@mui/icons-material';
import { FilterPopover } from '@views/pages/Reporting/Reports/Report/components/Report/components/Filters/Filters.components.tsx';

export const FiltersCard = () => {
  const {
    introspection,
    filters,
    handleCloseFiltersCard,
    isFiltersCardOpen,
    handleFilterChange,
    report,
    setReport,
    anchorEl,
    setAnchorEl,
    handleSaveFilter,
    handleRemoveFilter,
    handleApplyNewFilters,
    isApplyDisabled,
    isResetDisabled,
    setReportFilters,
    handleReset,
  } = useFiltersCard();

  return (
    <Box
      sx={{
        minWidth: '260px',
        width: '260px',
        boxShadow: 'initial',
        display: isFiltersCardOpen ? 'flex' : 'none',
        flexDirection: 'column',
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          justifyContent: 'space-between',
          width: '100%',
          padding: '0',
          pb: '1.5rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FilterAlt />
          <Typography variant='subtitle1'>Filters</Typography>
        </Box>
        <Box
          onClick={handleCloseFiltersCard}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            ':hover': {
              cursor: 'pointer',
              bgcolor: theme => theme.palette.grey[50],
              borderRadius: '6px',
            },
            height: '30px',
            width: '30px',
          }}
        >
          <ClearIcon />
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          pt: 1,
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 445px)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {report?.report_type === 'patients_report' && (
            <Select
              name='patient_report_type'
              clearable={false}
              options={patientReportTypeOptions}
              value={(report as IPatientReport)?.options?.patient_report_type}
              label='Show'
              sx={{
                bgcolor: theme.palette.common.white,
              }}
              onChange={event => {
                setReport({
                  ...report,
                  options: {
                    ...report?.options,
                    patient_report_type: event.target
                      .value as ReportPatientReportType,
                  } as any,
                });
                handleFilterChange({
                  ...filters,
                  options: {
                    ...filters?.options,
                    patient_report_type: event.target
                      .value as ReportPatientReportType,
                  } as any,
                });
              }}
            />
          )}

          <Select
            name='interval'
            clearable={false}
            options={reportTimeIntervalOptions}
            value={report?.interval}
            label='Time range'
            sx={{
              bgcolor: theme.palette.common.white,
            }}
            onChange={event => {
              handleFilterChange({
                ...filters,
                interval: event.target.value as ReportTimeInterval,
              });
              setReport({
                ...report,
                interval: event.target.value as ReportTimeInterval,
              });
            }}
          />
          {report?.interval === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
              <DatePicker
                name='from_date'
                label='From'
                value={
                  report?.custom_timeframe?.from_date
                    ? dayjs(report.custom_timeframe.from_date)
                    : null
                }
                onChange={val => {
                  const formattedValue = val?.isValid()
                    ? dayjs(val).format('YYYY-MM-DD')
                    : null;

                  setReport({
                    ...report,
                    custom_timeframe: {
                      ...report?.custom_timeframe,
                      from_date: formattedValue,
                    } as any,
                  });
                  handleFilterChange({
                    ...filters,
                    custom_timeframe: {
                      ...filters?.custom_timeframe,
                      from_date: formattedValue,
                    } as any,
                  });
                }}
              />
              <DatePicker
                name='to_date'
                label='To'
                value={
                  report?.custom_timeframe?.to_date
                    ? dayjs(report.custom_timeframe.to_date)
                    : null
                }
                minDate={dayjs(report?.custom_timeframe?.from_date).add(
                  1,
                  'day',
                )}
                onChange={val => {
                  const formattedValue = val?.isValid()
                    ? dayjs(val).format('YYYY-MM-DD')
                    : null;

                  setReport({
                    ...report,
                    custom_timeframe: {
                      ...report?.custom_timeframe,
                      to_date: formattedValue,
                    } as any,
                  });

                  handleFilterChange({
                    ...filters,
                    custom_timeframe: {
                      ...filters?.custom_timeframe,
                      to_date: formattedValue,
                    } as any,
                  });
                }}
              />
            </LocalizationProvider>
          )}

          {report?.report_type === 'tasks_report' && (
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={
                <Checkbox
                  name='include_future_tasks'
                  sx={{ py: 0 }}
                  checked={!!report?.options?.include_future_tasks}
                  onChange={event => {
                    setReport({
                      ...report,
                      options: {
                        ...report?.options,
                        include_future_tasks: event.target.checked,
                      } as any,
                    });
                    handleFilterChange({
                      ...filters,
                      options: {
                        ...filters?.options,
                        include_future_tasks: event.target.checked,
                      } as any,
                    });
                  }}
                />
              }
              label='Include future tasks'
            />
          )}

          {(filters.options?.$and ?? []).map((field, index) => {
            const fieldIntrospection = introspection?.filters?.find(
              o => o.field === field.field,
            );
            return (
              <Box
                key={`${field.field}-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: theme.palette.common.white,
                    maxWidth: 'calc(100% - 48px)',
                  }}
                >
                  <FilterTypeMapper
                    entry={{
                      field: field.field,
                      operator: field.operator,
                      value: field.value as string | string[],
                      label:
                        introspection?.filters?.find(
                          f => f.field === field.field,
                        )?.label ?? field.field,
                    }}
                    introspection={fieldIntrospection}
                    handleOnChange={value => {
                      const currentAnd = filters?.options?.$and ?? [];

                      const updatedAnd = currentAnd.map(entry =>
                        entry.field === field.field
                          ? { ...entry, value: value }
                          : entry,
                      );

                      handleFilterChange({
                        ...filters,
                        options: {
                          ...filters?.options,
                          $and: updatedAnd,
                        } as any,
                      });
                      setReport({
                        ...report,
                        options: {
                          ...report?.options,
                          $and: updatedAnd,
                        } as any,
                      });
                      setReportFilters(updatedAnd as any);
                    }}
                  />
                </Box>
                <IconButton onClick={() => handleRemoveFilter(field.field)}>
                  <Delete />
                </IconButton>
              </Box>
            );
          })}
        </Box>

        <Button
          sx={{ height: '30px', width: 'fit-content' }}
          color='primary'
          startIcon={<Add sx={{ height: '20px' }} color='primary' />}
          onClick={event => setAnchorEl(event.currentTarget)}
        >
          ADD
        </Button>

        {anchorEl && (
          <FilterPopover
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            handleApply={handleSaveFilter}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: '0.5rem', width: '100%', mt: 2 }}>
        <Button
          variant='outlined'
          onClick={handleReset}
          disabled={isResetDisabled}
          sx={{ flex: 1 }}
        >
          {t('general.reset')}
        </Button>
        <Button
          variant='contained'
          color='secondary'
          sx={{ flex: 1 }}
          disabled={isApplyDisabled}
          onClick={handleApplyNewFilters}
        >
          {t('general.save')}
        </Button>
      </Box>
    </Box>
  );
};
